import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { fetchUserInfo, teamPhoto } from '../../utils';
import Base from '../Base';
import Icon from '../../components/common/Icon';
import NotFound from '../../components/common/NotFound';
import '../user/PublicProfile.css';
import './EventList.css';

// The search box filters the own list and the other event's alike.
const matches = (q) => (team) =>
  (team.team_name?.toLowerCase() || '').includes(q) ||
  (team.company_name?.toLowerCase() || '').includes(q);

const Teams = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUserInfo('/teams', setUserInfo, setLoading, setError, ['selectedEventId']);
  }, []);

  const filteredTeams = useMemo(() => {
    if (!userInfo?.teams) return null;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return userInfo.teams;
    return userInfo.teams.filter(matches(q));
  }, [userInfo, searchQuery]);

  // The other event on the same day at the same venue (HT and PLG Dubai) — for information only,
  // so its cards open the public team page straight away and never set the otherTeamId context.
  const siblingEvents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (userInfo?.sibling_events || [])
      .map((s) => ({ ...s, items: q ? s.items.filter(matches(q)) : s.items }))
      .filter((s) => s.items.length > 0);
  }, [userInfo, searchQuery]);

  const renderCard = (team, linkProps) => {
    const usingCompanyLogo = !team.team_picture && !!team.company_picture;
    const photo = teamPhoto(team.team_picture, team.company_picture);
    return (
      <Link
        key={team.team_id}
        {...linkProps}
        className={`pp-team-card ${usingCompanyLogo ? 'el-team-card--logo' : ''}`}
      >
        <img
          src={photo}
          alt=""
          className={`pp-team-img ${usingCompanyLogo ? 'el-team-img--contain' : ''}`}
        />
        <div className="pp-team-fade" />
        <div className="pp-team-meta">
          <div className="pp-team-name">{team.team_name}</div>
          {team.company_name && (
            <div className="pp-team-company">{team.company_name}</div>
          )}
        </div>
      </Link>
    );
  };

  return (
    <Base activeNav="home">
      <div className="pp-page">
        <div className="el-topbar">
          <button type="button" className="el-back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <Icon name="angle-left" />
          </button>
          <h1 className="el-page-title">{t('teams')}</h1>
        </div>

        <div className="el-search-bar">
          <Icon name="search" className="el-search-icon" />
          <input
            type="text"
            className="el-search-input"
            placeholder={t('searchTeams')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="el-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear"
            >
              <Icon name="cross" />
            </button>
          )}
        </div>

        {filteredTeams && filteredTeams.length > 0 ? (
          <div className="pp-team-grid">
            {filteredTeams.map((team) => renderCard(team, {
              to: '/team',
              onClick: () => sessionStorage.setItem('otherTeamId', team.team_id),
            }))}
          </div>
        ) : (
          <div className="el-empty">
            <NotFound translationKey="noTeamsFound" loading={loading} />
          </div>
        )}

        {siblingEvents.map((s) => (
          <section key={s.event_id}>
            <h2 className="el-sibling-head">
              {s.logo && <img src={s.logo} alt="" className="el-sibling-logo" />}
              {t('siblingListTitle', { event: s.event_name })}
            </h2>
            <div className="pp-team-grid el-sibling-grid">
              {s.items.map((team) => renderCard(team, { to: `/public-team/${team.team_id}` }))}
            </div>
          </section>
        ))}
      </div>
    </Base>
  );
};

export default Teams;
